import { startNewTrace, startSpan } from '@sentry/vue';
import { defineStore } from 'pinia';

import { useGlobalStore } from '@/common/globalStore';
import { gql as gqlFn } from '@/graphql/__generated__';
import { ProjectImportStrategy } from '@/graphql/__generated__/graphql';
import { FileUpload } from '@/helpers/files/upload';
import { useApolloClient } from '@/plugins/apollo/apollo';
import { ALWAYS_SAMPLE_SPANS } from '@/plugins/sentry';
import { useUserStore } from '@/services/store/user/store';
import { useFileUploadService } from '@/services/upload/useFileUploadService';

const startProjectScheduleImportMutation = gqlFn(/* GraphQL */ `
  mutation StartProjectScheduleImport($input: StartProjectScheduleImportInput!) {
    startProjectScheduleImport(input: $input) {
      fileUpload {
        url
        fields {
          key
          value
        }
      }
    }
  }
`);

// NOTE: Copied from import repo for now
export interface InvalidOrder {
  name: string;
  reason: 'invalidDateRange' | 'invalidParent' | 'invalidTrade' | 'invalidCalendar' | 'misc';
  message?: string;
}

export interface InvalidMilestone {
  name: string;
  reason: 'invalidDate' | 'invalidParent' | 'invalidTrade' | 'misc';
  message?: string;
}

export interface InvalidDependency {
  fromName: string;
  toName: string;
  reason: 'invalidConnector' | 'duplicate' | 'nonLeafConnector' | 'misc' | 'cyclic';
  message?: string;
}

export interface InvalidCalendar {
  name: string;
  reason: 'misc';
  message?: string;
}

export interface InvalidWbsSection {
  name: string;
  reason: 'invalidParent' | 'misc';
  message?: string;
}

export interface InvalidTrade {
  name: string;
  reason: 'misc';
  message?: string;
}

export interface Collision {
  fromName: string;
  toName: string;
  reason: 'hourlyTransformation' | 'preExisting' | 'misc';
  message?: string;
}

export interface Issues {
  invalidEntities: {
    orders: InvalidOrder[];
    milestones: InvalidMilestone[];
    dependencies: InvalidDependency[];
    calendars: InvalidCalendar[];
    wbsSections: InvalidWbsSection[];
    trades: InvalidTrade[];
  };
  collisions: Collision[];
}
export interface LLMResponse {
  response: string;
  meta: {
    model: string;
    taskId: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cachedTokens: number;
    runtimeInMs: number;
    estimatedCost: number;
  };
}

export interface ProjectScheduleImportMetadata {
  date: string; // ISO string
  runtimeInMs: number;
  input: {
    fileName: string;
    fileSizeInMB: number;
    numberOfMilestones: number;
    numberOfDependencies: number;
    numberOfCalendars: number;
    numberOfRows: number;
    numberOfOrders: number;
    numberOfSummaryRows: number;
    timeRange: {
      start: string; // ISO string
      end: string; // ISO string
    };
  };
  output: {
    numberOfOrders: number;
    numberOfWbsSections: number;
    numberOfTrades: number;
    numberOfMilestones: number;
    numberOfDependencies: number;
    numberOfCalendars: number;
    timeRange: {
      start: string; // ISO string
      end: string; // ISO string
    };
    issues: Issues;
  };
  details?: {
    llm: {
      totalCost: number;
      totalRuntime: number;
      totalInputTokens: number;
      totalOutputTokens: number;
      requests: LLMResponse['meta'][];
    };
  };
  // NOTE: This one is not part of import type
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export const useProjectScheduleImportStore = defineStore(
  'project-schedule-import-store',
  () => {
    const globalStore = useGlobalStore();
    const userStore = useUserStore();

    const importMetadata = ref<Record<string, ProjectScheduleImportMetadata | null>>({});

    // NOTE: This one is only here temporarily to display some file info, as we don't save it in BE yet
    const importFileNames = ref<Record<string, string>>({});
    const importFileSizes = ref<Record<string, number>>({});
    const setImportFileName = (projectId: string, fileName: string) => {
      importFileNames.value[projectId] = fileName;
    };

    const setImportFileSize = (projectId: string, fileSize: number) => {
      importFileSizes.value[projectId] = fileSize;
    };

    const startImport = async (args: {
      fileUpload: FileUpload;
      projectId: string;
      strategy: ProjectImportStrategy;
      customStrategy?: string;
    }) => {
      const client = useApolloClient();

      setImportFileName(args.projectId, args.fileUpload.file.name);
      setImportFileSize(args.projectId, args.fileUpload.file.size);

      await startNewTrace(() =>
        startSpan({ name: ALWAYS_SAMPLE_SPANS.ImportSchedule }, async () => {
          const result = await client.mutate({
            mutation: startProjectScheduleImportMutation,
            variables: {
              input: {
                project: args.projectId,
                fileInput: {
                  filename: args.fileUpload.file.name,
                  filesize: args.fileUpload.file.size,
                  mimetype: args.fileUpload.file.type,
                },
                strategy: args.strategy,
                ...(args.customStrategy && { customStrategy: args.customStrategy }),
              },
            },
          });

          const importMutationResult = result.data?.startProjectScheduleImport?.fileUpload;
          if (!importMutationResult) throw new Error('Failed to start import.');

          await startSpan({ name: 'Upload MPP' }, () =>
            useFileUploadService().upload(
              args.fileUpload.file,
              importMutationResult.url,
              importMutationResult.fields,
            ),
          );
        }),
      );
    };

    // NOTE: The incoming metadata result is untyped for now
    const setImportMetadata = (
      metadata: any | null,
      user: { id: string; firstName: string; lastName: string } | null,
    ) => {
      importMetadata.value[globalStore.currentProjectId ?? ''] = {
        ...metadata,
        user: user ?? userStore.ownUser ?? { id: '', firstName: '', lastName: '' },
      };
    };

    const clearImportMetadata = () => {
      importMetadata.value[globalStore.currentProjectId ?? ''] = null;
    };

    const currentImportMetadata = computed(
      () => importMetadata.value[globalStore.currentProjectId ?? ''] ?? null,
    );

    const currentImportFileName = computed(
      () => importFileNames.value[globalStore.currentProjectId ?? ''] ?? null,
    );

    const currentImportFileSize = computed(
      () => importFileSizes.value[globalStore.currentProjectId ?? ''] ?? null,
    );

    const setDummyMetadata = () => {
      importMetadata.value[globalStore.currentProjectId ?? ''] = dummyMetadata({
        id: userStore.ownUser?.id ?? '',
        firstName: userStore.ownUser?.firstName ?? '',
        lastName: userStore.ownUser?.lastName ?? '',
      });
      importFileNames.value[globalStore.currentProjectId ?? ''] = dummyFileName;
    };

    return {
      startImport,
      setImportMetadata,
      clearImportMetadata,
      importMetadata,
      importFileNames,
      currentImportMetadata,
      currentImportFileName,
      currentImportFileSize,
      setDummyMetadata,
    };
  },
  {
    persist: {
      pick: ['importMetadata', 'importFileNames', 'importFileSizes'],
    },
  },
);

const dummyMetadata = (user: {
  id: string;
  firstName: string;
  lastName: string;
}): ProjectScheduleImportMetadata => ({
  date: new Date().toISOString(),
  runtimeInMs: 30000,
  input: {
    fileName: 'LP8_TestProjekt.mpp',
    fileSizeInMB: 100,
    numberOfMilestones: 10,
    numberOfDependencies: 300,
    numberOfCalendars: 3,
    numberOfRows: 500,
    numberOfOrders: 100,
    numberOfSummaryRows: 400,
    timeRange: {
      start: new SchedulingDate(2025, 0, 1).toISOString(),
      end: new SchedulingDate(2025, 11, 31).toISOString(),
    },
  },
  output: {
    numberOfOrders: 95,
    numberOfWbsSections: 50,
    numberOfTrades: 10,
    numberOfMilestones: 6,
    numberOfDependencies: 296,
    numberOfCalendars: 3,
    timeRange: {
      start: new SchedulingDate(2025, 0, 1).toISOString(),
      end: new SchedulingDate(2025, 11, 31).toISOString(),
    },
    issues: {
      invalidEntities: {
        orders: [
          {
            name: 'Vorgang 12',
            reason: 'invalidDateRange',
          },
          {
            name: 'Vorgang 48',
            reason: 'invalidTrade',
          },
          {
            name: 'Vorgang 49',
            reason: 'invalidCalendar',
          },
          {
            name: 'Vorgang 52',
            reason: 'invalidParent',
          },
          {
            name: 'Vorgang 53',
            reason: 'misc',
          },
        ],
        milestones: [
          {
            name: 'Meilenstein 1',
            reason: 'invalidDate',
          },
          {
            name: 'Meilenstein 2',
            reason: 'invalidParent',
          },
          {
            name: 'Meilenstein 3',
            reason: 'invalidTrade',
          },
          {
            name: 'Meilenstein 4',
            reason: 'misc',
          },
        ],
        dependencies: [
          {
            fromName: 'Vorgang 52',
            toName: 'Vorgang 55',
            reason: 'invalidConnector',
          },
          {
            fromName: 'Vorgang 2',
            toName: 'Vorgang 3',
            reason: 'duplicate',
          },
          {
            fromName: 'Bauausführung',
            toName: 'Vorgang 13',
            reason: 'nonLeafConnector',
          },
          {
            fromName: 'Vorgang 78',
            toName: 'Vorgang 81',
            reason: 'misc',
          },
        ],
        calendars: [],
        wbsSections: [],
        trades: [],
      },
      collisions: [
        {
          fromName: 'Vorgang 1',
          toName: 'Vorgang 2',
          reason: 'hourlyTransformation',
        },
        {
          fromName: 'Vorgang 22',
          toName: 'Vorgang 23',
          reason: 'preExisting',
        },
        {
          fromName: 'Vorgang 3',
          toName: 'Vorgang 4',
          reason: 'preExisting',
        },
        {
          fromName: 'Vorgang 34',
          toName: 'Vorgang 44',
          reason: 'misc',
        },
      ],
    },
  },
  user: {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
  },
});

const dummyFileName = 'LP8_TestProjekt.mpp';
